import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';

const OffersManagement = () => {
    const [offers, setOffers] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ productId: '', size: '', title: '', offerPrice: '' });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [offersRes, productsRes] = await Promise.all([
                apiClient.get('/offers', { headers }),
                apiClient.get('/products')
            ]);

            setOffers(offersRes.data);
            setProducts(productsRes.data);
        } catch (error) {
            console.error('Error fetching offers data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({ productId: '', size: '', title: '', offerPrice: '' });
    };

    const handleEditClick = (offer) => {
        setEditingId(offer._id);
        setFormData({
            productId: offer.product?._id || '',
            size: offer.size || '',
            title: offer.title || '',
            offerPrice: offer.offerPrice
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (offer) => {
        if (!window.confirm(`Delete offer "${offer.title}"?`)) return;
        try {
            const token = localStorage.getItem('token');
            await apiClient.delete(`/offers/${offer._id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.message || 'Error deleting offer');
        }
    };

    const toggleActive = async (offer) => {
        try {
            const token = localStorage.getItem('token');
            await apiClient.put(`/offers/${offer._id}`, { isActive: !offer.isActive }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchData();
        } catch (error) {
            alert('Error updating offer');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            if (editingId) {
                await apiClient.put(`/offers/${editingId}`, {
                    title: formData.title,
                    offerPrice: formData.offerPrice,
                    size: formData.size
                }, { headers });
                alert('Offer updated successfully!');
            } else {
                await apiClient.post('/offers', formData, { headers });
                alert('Offer created successfully!');
            }

            fetchData();
            resetForm();
        } catch (error) {
            alert(error.response?.data?.message || `Error ${editingId ? 'updating' : 'creating'} offer`);
        }
    };

    const selectedProduct = products.find(p => p._id === formData.productId);

    if (loading) return <div>Loading...</div>;

    return (
        <div className="p-4 md:p-6 bg-white shadow-md rounded-lg max-w-full overflow-hidden">
            <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-8 w-2 bg-orange-500 rounded-full"></div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight text-center">
                    Offers
                </h2>
            </div>

            <div className="mb-10 bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold mb-4 text-gray-700">{editingId ? 'Edit Offer' : 'Create New Offer'}</h3>
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select
                        name="productId"
                        value={formData.productId}
                        onChange={handleInputChange}
                        className="p-2 border rounded disabled:bg-gray-100 disabled:text-gray-400"
                        required={!editingId}
                        disabled={!!editingId}
                    >
                        <option value="">{editingId ? 'Product (fixed at creation)' : 'Select Product'}</option>
                        {products.map(p => (
                            <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                    </select>

                    <select name="size" value={formData.size} onChange={handleInputChange} className="p-2 border rounded">
                        <option value="">Any Pack Size</option>
                        {(selectedProduct?.variants || []).map(v => (
                            <option key={v.size} value={v.size}>{v.size}</option>
                        ))}
                    </select>

                    <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="Offer Title (e.g. Flash Sale)" className="p-2 border rounded" required />
                    <input type="number" name="offerPrice" value={formData.offerPrice} onChange={handleInputChange} placeholder="Offer Price (₹)" className="p-2 border rounded" required min="0" step="0.01" />

                    <div className="md:col-span-2 mt-2 flex gap-3">
                        <button type="submit" className="flex-1 bg-orange-600 text-white font-bold py-3 rounded-lg hover:bg-orange-700">
                            {editingId ? 'Save Changes' : 'Create Offer'}
                        </button>
                        {editingId && (
                            <button type="button" onClick={resetForm} className="px-8 bg-gray-300 text-gray-700 font-bold py-3 rounded-lg hover:bg-gray-400">
                                Cancel
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <h3 className="text-xl font-bold mb-4 text-gray-700">Existing Offers</h3>
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-100 border-b">
                            <th className="p-3 font-semibold">Product</th>
                            <th className="p-3 font-semibold">Size</th>
                            <th className="p-3 font-semibold">Title</th>
                            <th className="p-3 font-semibold">Offer Price</th>
                            <th className="p-3 font-semibold">Stores Showing It</th>
                            <th className="p-3 font-semibold">Status</th>
                            <th className="p-3 font-semibold">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {offers.map(o => (
                            <tr key={o._id} className="border-b hover:bg-gray-50">
                                <td className="p-3 font-medium">{o.product?.name}</td>
                                <td className="p-3 text-sm">{o.size || 'Any'}</td>
                                <td className="p-3 text-sm">{o.title}</td>
                                <td className="p-3 font-bold text-green-700">₹{o.offerPrice}</td>
                                <td className="p-3 text-sm">{(o.enabledFranchises || []).length}</td>
                                <td className="p-3">
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${o.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {o.isActive ? 'ACTIVE' : 'INACTIVE'}
                                    </span>
                                </td>
                                <td className="p-3">
                                    <div className="flex flex-wrap gap-2">
                                        <button onClick={() => handleEditClick(o)} className="px-3 py-1 rounded text-sm text-white bg-blue-500 hover:bg-blue-600">Edit</button>
                                        <button onClick={() => toggleActive(o)} className={`px-3 py-1 rounded text-sm text-white ${o.isActive ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}>
                                            {o.isActive ? 'Deactivate' : 'Activate'}
                                        </button>
                                        <button onClick={() => handleDelete(o)} className="px-3 py-1 rounded text-sm text-white bg-gray-700 hover:bg-gray-800">Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {offers.length === 0 && (
                            <tr><td colSpan="7" className="p-4 text-center text-gray-500">No offers found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default OffersManagement;
